import { useScopeClass } from "@/components/ui/scope";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface Segment<T extends string> {
  key: T;
  label: string;
  icon?: LucideIcon;
  iconClassName?: string;
  badge?: ReactNode;
  testId?: string;
  activeClassName?: string;
}

interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[];
  value: T;
  onValueChange: (value: T) => void;
  variant?: "pill" | "underline";
  className?: string;
  activeClassName?: string;
  testId?: string;
}

export function SegmentedControl<T extends string>({
  segments,
  value,
  onValueChange,
  variant = "pill",
  className,
  activeClassName,
  testId,
}: SegmentedControlProps<T>) {
  // Admin keeps the original underline tabs (no uppercase, no 48px min-height,
  // neutral active text); the public site keeps the new MUI Tabs styling.
  const isAdmin = useScopeClass() === "admin-scope";
  if (variant === "underline") {
    return (
      <div
        className={cn("flex border-b gap-0.5 sm:gap-1 overflow-x-auto scrollbar-none", className)}
        data-testid={testId}
      >
        {segments.map((seg) => {
          const isActive = seg.key === value;
          const Icon = seg.icon;
          return (
            <button
              key={seg.key}
              type="button"
              onClick={() => onValueChange(seg.key)}
              className={cn(
                // Old MUI Tabs: 48px, uppercase 14px/500, green active text + 2px green underline.
                "flex min-h-12 items-center gap-1 sm:gap-1.5 px-4 py-3 text-sm font-medium uppercase whitespace-nowrap border-b-2 transition-colors",
                // Admin reverts to the original underline tab look.
                isAdmin && "min-h-0 px-2 sm:px-3 py-2 sm:text-base normal-case",
                isActive
                  ? isAdmin
                    ? "border-primary text-foreground"
                    : "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
              data-testid={seg.testId}
            >
              {Icon && <Icon className={cn("size-4 shrink-0", seg.iconClassName)} />}
              {seg.label}
              {seg.badge}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn("flex gap-0.5 rounded-full bg-muted p-1", className)} data-testid={testId}>
      {segments.map((seg) => {
        const isActive = seg.key === value;
        const Icon = seg.icon;
        return (
          <button
            key={seg.key}
            type="button"
            onClick={() => onValueChange(seg.key)}
            className={cn(
              "flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors",
              isActive
                ? (seg.activeClassName ??
                    activeClassName ??
                    "bg-background text-foreground shadow-sm")
                : "text-muted-foreground",
            )}
            data-testid={seg.testId}
          >
            {Icon && (
              <Icon
                className={cn(
                  "size-3.5 shrink-0",
                  seg.iconClassName,
                  !seg.iconClassName && "text-current",
                )}
              />
            )}
            {seg.label}
            {seg.badge}
          </button>
        );
      })}
    </div>
  );
}
